/**
 * Module dependencies.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { InputField } from 'app/components/atoms/forms/fields/input/input-field';
import { SelectField } from 'app/components/atoms/forms/fields/select/select-field';
import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { cn } from 'app/lib/utils';
import { AlertTriangle, Landmark, Loader2, Plus, RefreshCw, Wallet } from 'lucide-react';
import {
  useCreateCryptoGestWallet,
  useCryptoGestChains,
  useCryptoGestWallets,
  useSyncAllCryptoGestWallets
} from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { shortAddress } from '../format';
import { useDisplayMoney } from '../use-display-money';
import { AssetIcon } from '../asset-icon';

/**
 * Icon symbol per chain (the chain's brand token) for tracked wallets.
 */

const CHAIN_ICON_SYMBOL: Record<string, string> = {
  bitcoin: 'BTC',
  ethereum: 'ETH',
  polygon: 'POL',
  arbitrum: 'ARB',
  optimism: 'OP',
  base: 'ETH',
  bsc: 'BNB',
  avalanche: 'AVAX',
  solana: 'SOL',
  injective: 'INJ',
  icp: 'ICP',
  cosmos: 'ATOM',
  cardano: 'ADA',
  algorand: 'ALGO',
  near: 'NEAR',
  multiversx: 'EGLD',
  stacks: 'STX',
  sui: 'SUI',
  polkadot: 'DOT',
  aptos: 'APT',
  xrp: 'XRP'
};

/**
 * Export `CryptoGestWallets` component.
 */

export function CryptoGestWallets(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { format: usd } = useDisplayMoney();
  const { data: wallets, isLoading } = useCryptoGestWallets();
  const syncAll = useSyncAllCryptoGestWallets();
  const [adding, setAdding] = useState(false);

  const anySyncing = !!wallets?.some(wallet => wallet.syncStatus === 'syncing');
  const hasSyncable = !!wallets?.some(wallet => wallet.type === 'tracked' || wallet.type === 'exchange');

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex items-center justify-between'}>
        <Title>{translate('cryptoGest.wallet.title')}</Title>

        <div className={'flex items-center gap-2'}>
          {hasSyncable && (
            <Button disabled={syncAll.isLoading || anySyncing} onClick={() => syncAll.mutate()} variant={'outline'}>
              <RefreshCw className={cn('h-4 w-4', (syncAll.isLoading || anySyncing) && 'animate-spin')} />

              {translate('cryptoGest.wallet.syncAll')}
            </Button>
          )}

          <Button onClick={() => setAdding(true)}>
            <Plus className={'h-4 w-4'} />

            {translate('cryptoGest.wallet.add')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className={'grid place-items-center py-20'}>
          <Spinner />
        </div>
      ) : !wallets || wallets.length === 0 ? (
        <div
          className={
            'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center'
          }
        >
          <div className={'text-base font-semibold text-foreground'}>{translate('cryptoGest.wallet.empty.title')}</div>

          <p className={'max-w-md text-sm text-muted-foreground'}>{translate('cryptoGest.wallet.empty.description')}</p>

          <Button className={'mt-2'} onClick={() => setAdding(true)}>
            {translate('cryptoGest.wallet.add')}
          </Button>
        </div>
      ) : (
        <div className={'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'}>
          {wallets.map(wallet => (
            <button
              className={
                'relative flex flex-col gap-3 rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary/40'
              }
              key={wallet.id}
              onClick={() => navigate(`/dashboard/crypto-gest/wallets/${wallet.id}`)}
              type={'button'}
            >
              {wallet.syncStatus === 'syncing' && (
                <Loader2 className={'absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground'} />
              )}

              <div className={'flex items-center gap-3'}>
                <div className={'grid h-9 w-9 place-items-center rounded-lg text-primary'}>
                  {wallet.type === 'tracked' ? (
                    <AssetIcon className={'h-7 w-7'} symbol={CHAIN_ICON_SYMBOL[wallet.chain ?? ''] ?? 'BTC'} />
                  ) : wallet.type === 'exchange' ? (
                    <Landmark className={'h-5 w-5'} />
                  ) : (
                    <Wallet className={'h-5 w-5'} />
                  )}
                </div>

                <div className={'min-w-0'}>
                  <div className={'flex items-center gap-1.5'}>
                    <span className={'truncate font-semibold text-foreground'}>{wallet.name}</span>

                    {wallet.hasHoldings && !wallet.txCount && (
                      <span className={'inline-flex shrink-0'} title={translate('cryptoGest.wallet.holdingsNoTx')}>
                        <AlertTriangle className={'h-4 w-4 text-amber-500'} />
                      </span>
                    )}
                  </div>

                  <div className={'truncate text-xs capitalize text-muted-foreground'}>
                    {wallet.type === 'tracked'
                      ? `${wallet.chainLabel} · ${shortAddress(wallet.address)}`
                      : wallet.type === 'exchange'
                      ? wallet.provider ?? translate('cryptoGest.type.exchange')
                      : translate(`cryptoGest.type.${wallet.type}`)}
                  </div>
                </div>
              </div>

              <div className={'flex items-end justify-between gap-2'}>
                <div className={'flex items-center gap-2'}>
                  <Badge variant={'secondary'}>{translate(`cryptoGest.type.${wallet.type}`)}</Badge>

                  {wallet.syncStatus === 'error' && (
                    <Badge variant={'destructive'}>{translate('cryptoGest.wallet.syncError')}</Badge>
                  )}
                </div>

                <div className={'text-right'}>
                  <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>
                    {translate('cryptoGest.labels.value')}
                  </div>

                  <div className={"font-['Oswald'] text-lg font-semibold tabular-nums text-foreground"}>
                    {usd(wallet.valueUsd)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <AddWalletDialog onOpenChange={setAdding} open={adding} />
    </div>
  );
}

/**
 * Add-wallet dialog.
 */

function AddWalletDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: chains } = useCryptoGestChains();
  const create = useCreateCryptoGestWallet();
  const [kind, setKind] = useState<'tracked' | 'manual' | 'exchange' | 'nexo'>('tracked');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({
    mode: 'all',
    defaultValues: { name: '', chain: 'bitcoin', address: '', provider: 'binance', apiKey: '', apiSecret: '' }
  });

  const close = () => {
    reset();
    setKind('tracked');
    onOpenChange(false);
  };

  const onSubmit = async (values: any) => {
    try {
      await create.mutateAsync({
        name: values.name,
        type: kind,
        chain: kind === 'tracked' ? values.chain : undefined,
        address: kind === 'tracked' ? values.address : undefined,
        provider: kind === 'exchange' ? values.provider : undefined,
        apiKey: kind === 'exchange' ? values.apiKey : undefined,
        apiSecret: kind === 'exchange' ? values.apiSecret : undefined
      });

      success({ message: translate('cryptoGest.wallet.created') });
      close();
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  return (
    <Dialog onOpenChange={value => (value ? onOpenChange(true) : close())} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate('cryptoGest.wallet.add')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Type toggle */}
          <div className={'mb-4 grid grid-cols-2 gap-2'}>
            {(['tracked', 'manual', 'exchange', 'nexo'] as const).map(option => (
              <button
                className={cn(
                  'flex flex-col items-start justify-start rounded-lg border p-3 text-left transition-colors',
                  kind === option ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                )}
                key={option}
                onClick={() => setKind(option)}
                type={'button'}
              >
                <div className={'text-sm font-semibold text-foreground'}>{translate(`cryptoGest.type.${option}`)}</div>

                <div className={'mt-0.5 text-xs text-muted-foreground'}>
                  {translate(`cryptoGest.type.${option}Hint`)}
                </div>
              </button>
            ))}
          </div>

          <InputField
            control={control}
            label={translate('cryptoGest.form.name')}
            name={'name'}
            placeholder={translate('cryptoGest.form.namePlaceholder')}
            rules={{ required: translate('common.errors.form.required') }}
          />

          {kind === 'tracked' && (
            <>
              <SelectField
                control={control}
                label={translate('cryptoGest.form.chain')}
                name={'chain'}
                options={(chains ?? []).map(chain => ({ value: chain.key, label: chain.label }))}
                rules={{ required: translate('common.errors.form.required') }}
              />

              <InputField
                control={control}
                label={translate('cryptoGest.form.address')}
                name={'address'}
                placeholder={translate('cryptoGest.form.addressPlaceholder')}
                rules={{ required: translate('common.errors.form.required') }}
              />

              <p className={'-mt-2 mb-2 text-xs text-muted-foreground'}>{translate('cryptoGest.form.addressSafety')}</p>
            </>
          )}

          {kind === 'exchange' && (
            <>
              <SelectField
                control={control}
                label={translate('cryptoGest.form.provider')}
                name={'provider'}
                options={[{ value: 'binance', label: 'Binance' }]}
                rules={{ required: translate('common.errors.form.required') }}
              />

              <InputField
                control={control}
                label={translate('cryptoGest.form.apiKey')}
                name={'apiKey'}
                placeholder={translate('cryptoGest.form.apiKeyPlaceholder')}
                rules={{ required: translate('common.errors.form.required') }}
              />

              <InputField
                control={control}
                label={translate('cryptoGest.form.apiSecret')}
                name={'apiSecret'}
                placeholder={translate('cryptoGest.form.apiSecretPlaceholder')}
                rules={{ required: translate('common.errors.form.required') }}
                type={'password'}
              />

              <p className={'-mt-2 mb-2 text-xs text-muted-foreground'}>
                {translate('cryptoGest.form.exchangeSafety')}
              </p>
            </>
          )}

          <div className={'mt-2 flex justify-end gap-2'}>
            <Button onClick={close} type={'button'} variant={'outline'}>
              {translate('common.actions.cancel')}
            </Button>

            <SubmitButton
              className={'mt-0'}
              disabled={isSubmitting}
              label={translate('cryptoGest.wallet.add')}
              loading={create.isLoading}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
